import React, { useCallback, useEffect, useMemo, useState } from "react";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { FormattedMessage, useIntl } from "react-intl";
import { Card, Container, Flex, IconContainer, Number, Text } from "./styles";
import { AttendenceIcon, ClockIcon, GraphIcon } from "./components/Icons";
import StaffDataTable from "./components/table";
import { useDispatch, useSelector } from "react-redux";
import {
  getStaffAttendenceTableInfo,
  getStaffAttendenceInfo,
  resetStaffAccess,
} from "../../redux/modules/staff-acess/actions";
import SelectFilter from "./components/SelectFilter";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../service/reportFilter.service";

const DEFAULT_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = 10;

const StaffAssistance = () => {
  const intl = useIntl();
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const dispatch = useDispatch();

  const { token } = useSelector((state) => state.auth);
  const { staffData, pageInfo, metrics } = useSelector(
    (state) => state.staff_access
  );

  const roleTypes = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "administrator" }),
        value: "ADMINISTRATOR",
      },
      {
        label: intl.formatMessage({ id: "staff" }),
        value: "STAFF",
      },
    ],
    [intl]
  );

  useEffect(() => {
    let mounted = true;

    getReportFilterYearsService({ token, active: true })
      .then((result) => {
        if (mounted) {
          setYears(
            (result?.data?.output ?? []).map((year) => ({
              label: String(year),
              value: year,
            }))
          );
        }
      })
      .catch(() => {
        if (mounted) {
          setYears([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const handleChangeYear = useCallback(
    (year) => {
      setSelectedEvent("");
      setEvents([]);

      if (!year) {
        return;
      }

      getReportFilterEventsByYearService({ token, year, active: true })
        .then((result) => {
          setEvents(
            (result?.data?.output ?? [])
              .map((event) => ({
                label: event.eventEditionName,
                value: event.idNumber,
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          );
        })
        .catch(() => setEvents([]));
    },
    [token]
  );

  useEffect(() => {
    return () => {
      dispatch(resetStaffAccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback(() => {
    const params = {
      sortBy,
      sortDirection: sortDirection.toUpperCase(),
      page: currentPage - 1,
      size: rowsPerPage,
      eventId: selectedEvent,
      roleType: selectedType,
    };

    return dispatch(getStaffAttendenceTableInfo(params));
  }, [
    currentPage,
    dispatch,
    rowsPerPage,
    selectedEvent,
    selectedType,
    sortBy,
    sortDirection,
  ]);

  const handleAttendanceInfo = useCallback(() => {
    const params = {
      eventId: selectedEvent,
      roleType: selectedType,
    };

    return dispatch(getStaffAttendenceInfo(params));
  }, [dispatch, selectedEvent, selectedType]);

  useEffect(() => {
    if (pageInfo) {
      setTotalRows(pageInfo.totalElements);
    }
  }, [pageInfo]);

  useEffect(() => {
    if (selectedYear && selectedEvent && selectedEvent !== "") {
      handleAttendanceInfo();
      handleSearch();
    }
  }, [handleSearch, selectedEvent, selectedType, selectedYear]);


  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({ id: "breadcumb.staff_assistance" })}
    >
      <Container>
        <Flex gap="10px" items="center" mb="24px">
          <Text>
            <FormattedMessage id="dashboard" />
          </Text>
          <SelectFilter
            label={"year"}
            options={years}
            setValue={(year) => {
              setSelectedYear(year);
              handleChangeYear(year);
            }}
            value={selectedYear}
            fit
          />
          <SelectFilter
            label={"event"}
            options={events}
            setValue={setSelectedEvent}
            value={selectedEvent}
            disabled={!selectedYear}
            fit
          />
          <SelectFilter
            label={"type"}
            options={roleTypes}
            setValue={setSelectedType}
            value={selectedType}
            fit
            hasAllOption
          />
        </Flex>

        <Flex gap="16px">
          <Flex direction="column" gap="16px" maxWidth="1489px" width="100%">
            <Flex flex width="100%">
              <CardsContainer metrics={metrics} />
            </Flex>
            <Flex flex width="100%">
              <TableContainer
                data={staffData}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={totalRows}
                setSortBy={setSortBy}
                setSortDirection={setSortDirection}
              />
            </Flex>
          </Flex>
          <AsideContainer metrics={metrics} />
        </Flex>
      </Container>
    </AnimatedMainContainer>
  );
};

export default StaffAssistance;

function CardsContainer({ metrics }) {
  return (
    <Flex flex gap="16px">
      <DashboardCard
        text={metrics.totalInteractions}
        title={"total_assistance"}
      />
      <DashboardCard
        text={metrics.averageServiceTime}
        title={"average_assistance_time"}
      />
      <DashboardCard
        text={metrics.peakTrafficTime ?? "00:00"}
        title={"peak_assistance"}
      />
    </Flex>
  );
}

function DashboardCard({ text, title }) {
  return (
    <Card flex>
      <Flex direction="column" gap="8px">
        <Icon title={title} />
        <div>
          <Text type="card-text">{text}</Text>
          <Text type="card-title">
            <FormattedMessage id={title} />
          </Text>
        </div>
      </Flex>
    </Card>
  );
}

function TableContainer({
  data,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  setSortBy,
  setSortDirection,
}) {
  return (
    <Card flex>
      <Flex direction="column" gap="16px">
        <Text type="subtitle">
          <FormattedMessage id={"ranking_of_attendants"} />
        </Text>
        <StaffDataTable
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalRows={totalRows}
          setSortBy={setSortBy}
          setSortDirection={setSortDirection}
          data={data}
        />
      </Flex>
    </Card>
  );
}

function AsideContainer({ metrics }) {
  return (
    <Flex width="270.19px" direction="column" gap="16px">
      <AsideCard title={"top_5_requests"} data={metrics.topRequests} />
      <AsideCard title={"top_5_attendants"} data={metrics.topAttendants} />
    </Flex>
  );
}
function AsideCard({ title, data }) {
  return (
    <Card flex noData={data.length === 0}>
      <Text type="aside-title" mb="16px">
        <FormattedMessage id={title} />
      </Text>

      <Flex direction="column" gap="16px">
        {data.map((_, i) => (
          <Flex key={i} items="center" gap="8px">
            <Number>{i + 1}</Number>
            <Text type="aside-text">{_}</Text>
          </Flex>
        ))}
      </Flex>
    </Card>
  );
}

function Icon({ title }) {
  const iconConfig = new Map();
  iconConfig.set("total_assistance", {
    icon: <AttendenceIcon />,
    background: "rgba(255, 214, 107, 0.2)",
  });
  iconConfig.set("average_assistance_time", {
    icon: <ClockIcon />,
    background: "rgba(255, 143, 107, 0.2)",
  });
  iconConfig.set("peak_assistance", {
    icon: <GraphIcon />,
    background: "rgba(91, 147, 255, 0.2)",
  });

  const selected = iconConfig.get(title);

  return (
    <IconContainer background={selected.background}>
      {selected.icon}
    </IconContainer>
  );
}
